import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Box,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Chip,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import Loading from "./Loading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";

const statusOptions = [
  { value: "", label: "All" },
  { value: "2", label: "Cancel" },
  { value: "3", label: "FollowUP" },
  { value: "4", label: "InProgress" },
  { value: "8", label: "Fixed" },
  { value: "9", label: "Flexible" },
  { value: "11", label: "Information" },
  { value: "7", label: "Pending" },
];

const OptClients = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;

  // State for follow-up filter (Autocomplete)
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  // State for status filter
  const [statusFilter, setStatusFilter] = useState("");

  // State for search term and pagination
  const [keyword, setKeyword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalClient, setTotalClient] = useState(0);

  // Autocomplete for follow-up users using the endpoint "/api/users/?position=1"
  const { data: followUps } = useFetchObjects(
    ["followup-users"],
    `users/?position=1`,
    token
  );

  const debouncedSetKeyword = useMemo(
    () => debounce((value) => setKeyword(value), 500),
    []
  );
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debouncedSetKeyword(e.target.value);
  };

  // Build query parameters for follow_up and status filters.
  const followUpQuery = selectedFollowUp
    ? `&follow_up=${selectedFollowUp.id}`
    : "";
  const statusQuery = statusFilter ? `&status=${statusFilter}` : "";

  const {
    data: clients,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["opt-clients"],
    `opt-clients/?search=${keyword}${followUpQuery}${statusQuery}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    if (clients && clients.count !== undefined) {
      setTotalClient(clients.count);
    }
  }, [clients]);

  useEffect(() => {
    refetch();
  }, [keyword, page, rowsPerPage, selectedFollowUp, statusFilter, refetch]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 2:
        return "warning";
      case 3:
        return "info";
      case 4:
        return "primary";
      case 8:
        return "success";
      case 9:
        return "secondary";
      case 11:
        return "error";
      case 7:
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ padding: "15px", width: "100%", mt: 2 }}>
        <Grid item xs={12} md={11.5}>
          <Typography
            variant="h5"
            fontWeight="bold"
            align="center"
            sx={{ padding: 2 }}
          >
            {t("call_center.optClientsList")}
          </Typography>

          {/* Search Field, Follow-Up Autocomplete, and Status Dropdown */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <TextField
              type="search"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder={t("call_center.searchByName")}
            />
            <Autocomplete
              options={followUps || []}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name}`
              }
              value={selectedFollowUp}
              onChange={(e, newValue) => {
                setSelectedFollowUp(newValue);
                setPage(1);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("call_center.followUpUser")}
                  variant="outlined"
                  size="small"
                />
              )}
              sx={{ width: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={statusFilter}
                onChange={handleStatusChange}
                displayEmpty
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Total Records Display */}
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("call_center.totalRecords")}: {totalClient}
          </Typography>

          <TableContainer>
            {isLoading ? (
              <Loading />
            ) : isError ? (
              <ErrorPage />
            ) : (
              <Table stickyHeader>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell align="center">{t("call_center.id")}</TableCell>
                    <TableCell align="center">
                      {t("call_center.patient")}
                    </TableCell>
                    <TableCell align="center">
                      {t("call_center.contact")}
                    </TableCell>
                    <TableCell align="center">
                      {t("call_center.procedure")}
                    </TableCell>
                    <TableCell align="center">
                      {t("call_center.date")}
                    </TableCell>
                    <TableCell align="center">
                      {t("call_center.status")}
                    </TableCell>
                    <TableCell align="center">
                      {t("call_center.address")}
                    </TableCell>
                    <TableCell align="center">
                      {t("call_center.referBy")}
                    </TableCell>
                    <TableCell align="center">Registration Date</TableCell>
                    <TableCell align="center">
                      {t("call_center.recordType")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients?.results?.map((client) => (
                    <TableRow key={client.id} hover>
                      <TableCell align="center">{client.id}</TableCell>
                      <TableCell align="center">{client.patient}</TableCell>
                      <TableCell align="center">{client.phone}</TableCell>
                      <TableCell align="center">
                        {client.procedure_type}
                      </TableCell>
                      <TableCell align="center">
                        {FormatDateTime(client.date)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={client.status_name}
                          color={getStatusColor(client.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {client.patient_address}
                      </TableCell>
                      <TableCell align="center">
                        {client.follow_up_first_name}{" "}
                        {client.follow_up_last_name}
                      </TableCell>
                      <TableCell align="center">
                        {FormatDateTime(client?.created_at)}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {client.record_type}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <FormControl size="small">
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <Pagination
              count={Math.ceil(totalClient / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              color="primary"
            />
          </Box>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default OptClients;
